import { IconBriefcase } from '@tabler/icons-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { type ClientData, getClientById } from '@/api/clients';
import { ClientDetailContent } from '@/components/client-detail-content';
import { type ClientStatus, StatusChip } from '@/components/status-chip';

export const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [client, setClient] = useState<ClientData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchClient = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await getClientById(id);
      setClient(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load client';
      if (/not found/i.test(message)) {
        setNotFound(true);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading client…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Client not found.</p>
        <Link to="/clients" className="text-sm underline underline-offset-4">
          Back to Clients
        </Link>
      </div>
    );
  }

  if (error || !client || !id) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? 'Failed to load client.'}
        </div>
        <Link to="/clients" className="text-sm underline underline-offset-4">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-4 md:gap-5 md:py-6 lg:px-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <header className="flex flex-col gap-4">
          <nav className="text-xs text-muted-foreground">
            <Link to="/clients" className="hover:text-foreground">
              Clients
            </Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{client.name}</span>
          </nav>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
                <IconBriefcase className="h-6 w-6 shrink-0" />
                {client.name}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {client.client_code ? `${client.client_code} · ` : ''}
                {client.company_email ?? 'No email on file'}
              </p>
            </div>
            {client.status && <StatusChip status={client.status as ClientStatus} type="client" />}
          </div>
        </header>

        <ClientDetailContent
          clientId={id}
          clientName={client.name}
          clientCode={client.client_code ?? undefined}
        />
      </div>
    </div>
  );
};
